import { Role } from '@/common/model/types';
import type { OnboardingStep, OnboardingStepGroup, OnboardingState } from './types';

const welcomeStep: OnboardingStep = {
  id: 'welcome',
  titleKey: 'welcome.title',
  descriptionKey: 'welcome.description',
  targetSelector: '',
  placement: 'center',
  requiredHover: false,
  order: 0,
};

const navigationStep: OnboardingStep = {
  id: 'navigation',
  titleKey: 'navigation.title',
  descriptionKey: 'navigation.description',
  targetSelector: '[data-onboarding="sidebar"]',
  placement: 'right',
  requiredHover: true,
  order: 1,
};

export const coachSteps: OnboardingStepGroup = {
  id: 'coach',
  title: 'Onboarding Coach & Club Admin',
  description: 'Guide pour les coachs et administrateurs de club',
  roles: [Role.COACH, Role.CLUB_ADMIN],
  steps: [
    welcomeStep,
    navigationStep,
    {
      id: 'coach-match-setup',
      titleKey: 'coach.match_setup.title',
      descriptionKey: 'coach.match_setup.description',
      targetSelector: '[data-onboarding="nav-new-match"]',
      placement: 'right',
      order: 10,
    },
    {
      id: 'coach-match-import',
      titleKey: 'coach.match_import.title',
      descriptionKey: 'coach.match_import.description',
      targetSelector: '[data-onboarding="nav-import"]',
      placement: 'right',
      order: 11,
    },
    {
      id: 'coach-trajectories',
      titleKey: 'coach.trajectories.title',
      descriptionKey: 'coach.trajectories.description',
      targetSelector: '[data-onboarding="nav-cartography"]',
      placement: 'right',
      condition: (state: OnboardingState) => state.isPhone !== true,
      order: 12,
    },
    {
      id: 'coach-stats',
      titleKey: 'coach.stats.title',
      descriptionKey: 'coach.stats.description',
      targetSelector: '[data-onboarding="nav-stats"]',
      placement: 'right',
      order: 13,
    },
    {
      id: 'coach-video',
      titleKey: 'coach.video.title',
      descriptionKey: 'coach.video.description',
      targetSelector: '[data-onboarding="nav-video"]',
      placement: 'right',
      order: 14,
    },
    {
      id: 'coach-teams',
      titleKey: 'coach.teams.title',
      descriptionKey: 'coach.teams.description',
      targetSelector: '[data-onboarding="nav-teams"]',
      placement: 'right',
      requiresAction: true,
      actionType: 'navigate',
      actionTarget: '/teams',
      order: 20,
    },
    {
      id: 'coach-opponent-teams',
      titleKey: 'coach.opponent_teams.title',
      descriptionKey: 'coach.opponent_teams.description',
      targetSelector: '[data-onboarding="opponent-teams-tab"]',
      placement: 'bottom',
      order: 21,
    },
    {
      id: 'coach-all-matches',
      titleKey: 'coach.all_matches.title',
      descriptionKey: 'coach.all_matches.description',
      targetSelector: '[data-onboarding="nav-matches"]',
      placement: 'right',
      order: 22,
    },
    {
      id: 'coach-membership',
      titleKey: 'coach.membership.title',
      descriptionKey: 'coach.membership.description',
      targetSelector: '[data-onboarding="nav-membership"]',
      placement: 'right',
      requiresAction: true,
      actionType: 'navigate',
      actionTarget: '/membership-requests',
      order: 23,
    },
    // Invite-by-email and invitation-link details moved to the "invite-members" tour (VOL-141).
    {
      id: 'coach-no-club',
      titleKey: 'coach.no_club.title',
      descriptionKey: 'coach.no_club.description',
      targetSelector: '',
      placement: 'center',
      condition: (state: OnboardingState) => !state.hasClub,
      order: 26,
    },
    {
      id: 'coach-tutorials',
      titleKey: 'coach.tutorials.title',
      descriptionKey: 'coach.tutorials.description',
      targetSelector: '[data-onboarding="nav-tutorials"]',
      placement: 'right',
      order: 30,
    },
    {
      id: 'coach-faq',
      titleKey: 'coach.faq.title',
      descriptionKey: 'coach.faq.description',
      targetSelector: '[data-onboarding="nav-faq"]',
      placement: 'right',
      order: 31,
    },
    {
      id: 'coach-feedback',
      titleKey: 'coach.feedback.title',
      descriptionKey: 'coach.feedback.description',
      targetSelector: '[data-onboarding="nav-feedback"]',
      placement: 'right',
      order: 32,
    },
    {
      id: 'coach-end',
      titleKey: 'coach.end.title',
      descriptionKey: 'coach.end.description',
      targetSelector: '',
      placement: 'center',
      order: 100,
    },
  ],
};

export const playerSteps: OnboardingStepGroup = {
  id: 'player',
  title: 'Onboarding Joueur',
  description: 'Guide pour les joueurs',
  roles: [Role.PLAYER],
  steps: [
    welcomeStep,
    {
      id: 'navigation-player',
      titleKey: 'player.navigation.title',
      descriptionKey: 'player.navigation.description',
      targetSelector: '[data-onboarding="sidebar"]',
      placement: 'right',
      requiredHover: true,
      order: 1,
    },
    {
      id: 'player-join-club',
      titleKey: 'player.join_club.title',
      descriptionKey: 'player.join_club.description',
      targetSelector: '[data-onboarding="nav-membership"]',
      placement: 'right',
      condition: (state: OnboardingState) => !state.hasClub,
      order: 10,
    },
    {
      id: 'player-no-club-explanation',
      titleKey: 'player.no_club_explanation.title',
      descriptionKey: 'player.no_club_explanation.description',
      targetSelector: '',
      placement: 'center',
      condition: (state: OnboardingState) => !state.hasClub,
      order: 11,
    },
    {
      id: 'player-join-team',
      titleKey: 'player.join_team.title',
      descriptionKey: 'player.join_team.description',
      targetSelector: '[data-onboarding="nav-membership"]',
      placement: 'right',
      requiresAction: true,
      actionType: 'navigate',
      actionTarget: '/membership',
      condition: (state: OnboardingState) => state.hasClub && !state.hasTeam,
      order: 12,
    },
    {
      id: 'player-stats',
      titleKey: 'player.stats.title',
      descriptionKey: 'player.stats.description',
      targetSelector: '[data-onboarding="nav-stats"]',
      placement: 'right',
      condition: (state: OnboardingState) => state.canOpenStats !== false,
      order: 100,
    },
    {
      id: 'player-tutorials',
      titleKey: 'player.tutorials.title',
      descriptionKey: 'player.tutorials.description',
      targetSelector: '[data-onboarding="nav-tutorials"]',
      placement: 'right',
      order: 110,
    },
    {
      id: 'player-faq',
      titleKey: 'player.faq.title',
      descriptionKey: 'player.faq.description',
      targetSelector: '[data-onboarding="nav-faq"]',
      placement: 'right',
      order: 111,
    },
    {
      id: 'player-feedback',
      titleKey: 'player.feedback.title',
      descriptionKey: 'player.feedback.description',
      targetSelector: '[data-onboarding="nav-feedback"]',
      placement: 'right',
      order: 112,
    },
    {
      id: 'player-end',
      titleKey: 'player.end.title',
      descriptionKey: 'player.end.description',
      targetSelector: '',
      placement: 'center',
      order: 200,
    },
  ],
};

export const allStepGroups: OnboardingStepGroup[] = [coachSteps, playerSteps];

export function getStepsForRole(role: Role, state: OnboardingState): OnboardingStep[] {
  const group = allStepGroups.find((g) => g.roles.includes(role));
  if (!group) return [];

  return group.steps
    .filter((step) => !step.condition || step.condition(state))
    .sort((a, b) => a.order - b.order);
}

function stepRequiresInteraction(step: OnboardingStep): boolean {
  return (
    (step.requiredHover ?? false) ||
    (step.requiresAction === true && step.actionType === 'navigate')
  );
}

export function getNextStep(
  role: Role,
  state: OnboardingState,
  completedSteps: string[],
  hoveredSections: string[],
): OnboardingStep | null {
  const steps = getStepsForRole(role, state);

  for (const step of steps) {
    const isCompleted = completedSteps.includes(step.id);
    const isHovered = hoveredSections.includes(step.id);
    const needsInteraction = stepRequiresInteraction(step);

    if (!isCompleted || (needsInteraction && !isHovered)) {
      return step;
    }
  }

  return null;
}

export function isOnboardingComplete(
  role: Role,
  state: OnboardingState,
  completedSteps: string[],
  hoveredSections: string[],
): boolean {
  const steps = getStepsForRole(role, state);

  return steps.every((step) => {
    const isCompleted = completedSteps.includes(step.id);
    const needsInteraction = stepRequiresInteraction(step);
    const isHovered = !needsInteraction || hoveredSections.includes(step.id);
    return isCompleted && isHovered;
  });
}
